'use strict';
/* Shared customer controls: the local preview and prepared theme use this renderer. */
(function (root) {
  const instances = new WeakMap();
  let instanceSequence = 0;
  function el(tag, text, attrs = {}) {
    const node = document.createElement(tag);
    if (text !== undefined && text !== null) node.textContent = text;
    for (const [key, value] of Object.entries(attrs)) {
      if (value !== undefined && value !== null) node.setAttribute(key, String(value));
    }
    return node;
  }
  function render(container, { sets, product, catalogue, selections = {}, quote, onChange }) {
    if (!instances.has(container)) instances.set(container, ++instanceSequence);
    const instance = instances.get(container);
    const active = document.activeElement;
    const focused = container.contains(active) && active.id ? { id: active.id, start: active.selectionStart, end: active.selectionEnd } : null;
    container.replaceChildren();
    const core = root.AMProductOptions;
    const fieldKey = (setId, fieldId) => JSON.stringify([setId, fieldId]);
    const controlId = (setId, fieldId) => {
      const set = encodeURIComponent(setId), field = encodeURIComponent(fieldId);
      return `am-option-${instance}-${set.length}-${set}-${field.length}-${field}`;
    };
    const visible = new Set((quote.visibleFields || []).map(f => fieldKey(f.setId, f.fieldId)));
    const variants = new Map(catalogue.flatMap(p => p.variants.map(v => [String(v.id), v])));
    for (const set of core.applicableSets(sets, product)) {
      const section = el('fieldset', null, { class: 'am-options-set' });
      section.append(el('legend', set.name));
      for (const field of set.fields) {
        if (!visible.has(fieldKey(set.id, field.id))) continue;
        const id = controlId(set.id, field.id);
        const box = el('div', null, { class: 'am-option-field', 'data-option-field': field.id });
        box.append(el('label', field.label + (field.required ? ' *' : ''), { for: id }));
        if (field.help) box.append(el('small', field.help));
        // Keep the customer's edit buffer separate from values normalized for the cart.
        const value = Object.prototype.hasOwnProperty.call(selections[set.id] || {}, field.id) ? selections[set.id][field.id] : quote.normalizedSelections?.[set.id]?.[field.id] ?? field.defaultValue;
        const change = val => onChange(set.id, field.id, val);
        const choiceLabel = choice => {
          const variant = choice.variantId && variants.get(String(choice.variantId));
          if (!variant) return choice.label + (choice.variantId ? ' · unavailable' : '');
          let price = '';
          try { price = ` (+${core.money(core.priceToCents(variant.price))})`; } catch (_) { /* Unknown price stays unorderable in the engine. */ }
          return choice.label + price + (variant.available === true ? '' : ' · unavailable');
        };
        if (field.type === 'select') {
          const select = el('select', null, { id, 'aria-label': field.label });
          select.append(el('option', 'Select an option', { value: '' }));
          for (const choice of field.choices) {
            const option = el('option', choiceLabel(choice), { value: choice.id });
            option.selected = value === choice.id;
            select.append(option);
          }
          select.value = value || '';
          select.addEventListener('change', () => change(select.value));
          box.append(select);
        } else if (field.type === 'radio' || field.type === 'checkbox') {
          const group = el('div', null, { role: 'group', 'aria-label': field.label, id });
          for (const choice of field.choices) {
            const label = el('label', null, { class: 'am-option-choice' });
            const choiceId = encodeURIComponent(choice.id);
            const input = el('input', null, { type: field.type, name: id, id: `${id}-choice-${choiceId.length}-${choiceId}`, value: choice.id });
            input.checked = field.type === 'checkbox' ? Array.isArray(value) && value.includes(choice.id) : value === choice.id;
            input.addEventListener('change', () => change(field.type === 'checkbox' ? [...group.querySelectorAll('input:checked')].map(i => i.value) : input.value));
            label.append(input, el('span', choiceLabel(choice)));
            group.append(label);
          }
          box.append(group);
        } else {
          const input = el(field.type === 'textarea' ? 'textarea' : 'input', null, { id, type: field.type === 'number' ? 'number' : 'text', placeholder: field.placeholder || '', 'aria-label': field.label });
          input.value = value ?? '';
          if (field.maxLength) input.maxLength = field.maxLength;
          if (field.type === 'number') {
            input.step = 'any';
            if (field.min !== undefined) input.min = field.min;
            if (field.max !== undefined) input.max = field.max;
          }
          // Re-rendering restores the active control and caret below, so required
          // validation and conditions update as the customer types.
          input.addEventListener('input', () => change(input.value));
          box.append(input);
        }
        const errors = (quote.errors || []).filter(e => e.setId === set.id && e.fieldId === field.id);
        if (errors.length) {
          box.append(el('small', errors.map(e => e.message).join(' '), { class: 'am-option-error', id: `${id}-error` }));
          for (const control of box.querySelectorAll('input,select,textarea')) {
            control.setAttribute('aria-invalid', 'true');
            control.setAttribute('aria-describedby', `${id}-error`);
          }
        }
        section.append(box);
      }
      container.append(section);
    }
    if (focused) {
      const replacement = document.getElementById(focused.id);
      if (replacement && container.contains(replacement)) {
        replacement.focus({ preventScroll: true });
        if (focused.start !== null && focused.start !== undefined && typeof replacement.setSelectionRange === 'function') {
          try { replacement.setSelectionRange(focused.start, focused.end); } catch (_) { /* Number controls do not support text selections. */ }
        }
      }
    }
  }
  root.AMProductOptionsRender = { render, el };
})(typeof window !== 'undefined' ? window : globalThis);
